import type {
	LWApiPublicSecureDataShareCreateParamsRawI,
	LWApiPublicSecureDataShareCreateResultRawI,
	LWApiPublicSecureDataShareListResultRawI,
} from '@liquidweb-dev/club-sauce/public/secureDataShare/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import toast from '@liquidweb-dev/undercurrent/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { actions as accountDetailsActions } from 'modules/api/account/detailsModule';
import { ACCOUNT_DETAILS_QUERY_KEY } from 'modules/queries/account/useDetails';
import api from 'modules/queries/api';
import { useDispatch } from 'react-redux';
import useSnackbarError from 'utility/hooks/useSnackbarError';
import { SECURE_DATA_SHARE_LIST_QUERY_KEY } from './useList';

const useSecureDataShareCreate = () => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const snackbarError = useSnackbarError();

	return useMutation<
		LWApiPublicSecureDataShareCreateResultRawI,
		ApiErrorI,
		LWApiPublicSecureDataShareCreateParamsRawI
	>({
		mutationFn: (params) => api.secureDataShare.apiRaw.create(params),
		onSuccess: (data, params) => {
			queryClient.invalidateQueries({
				queryKey: [SECURE_DATA_SHARE_LIST_QUERY_KEY],
			});

			// pessimistic update
			queryClient.setQueriesData<LWApiPublicSecureDataShareListResultRawI>(
				[SECURE_DATA_SHARE_LIST_QUERY_KEY],
				(current) => {
					if (!current) {
						return current;
					}

					return {
						...current,
						items: [
							{ ...data, description: params.description },
							...current.items,
						],
					};
				},
			);

			toast.success('Successfully created Secure Note');
		},
		onError: snackbarError,
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: [
					ACCOUNT_DETAILS_QUERY_KEY,
					{
						alsowith: [
							'managementPortal',
							'businessUnit',
							'customerLifecycle',
							'highlights',
							'referAFriend',
						],
					},
				],
			});

			// TODO DEV-3707 - remove once we no longer fetch account/details with redux api pattern
			dispatch(
				accountDetailsActions.invalidate({
					alsowith: [
						'managementPortal',
						'businessUnit',
						'customerLifecycle',
						'highlights',
						'referAFriend',
					],
				}),
			);
		},
	});
};

export default useSecureDataShareCreate;
